import React, { useState } from 'react';
import { serverApi } from '../api';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { urlMedia } from '../helpers/videosMedia';
import {
    prepararImagen,
    leerDimensionesVideo,
    dimensionesValidas,
    TEXTO_REQUISITO,
    MAX_LADO_IMAGEN,
} from '../helpers/mediaValidation';

const MySwal = withReactContent(Swal);

const specStyle = { fontSize: 11, color: '#64748b', marginTop: 4 };

/**
 * Zona de carga (drag & drop) para un medio de pantalla: imagen o video.
 * Mismo lenguaje visual que ImageDropzone (Mi Empresa), pero además:
 *  - valida resolución mínima (evita que se vea pixelado en las pantallas)
 *  - reescala/comprime las imágenes grandes antes de subir
 *  - sube a /videos/upload-media y devuelve la URL relativa
 */
export const MediaDropzone = ({
    tipoMedia = 'imagen',
    value = '',
    onChange,
    label,
    disabled = false,
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [info, setInfo] = useState(null);

    const esVideo = tipoMedia === 'video';
    const safeValue = typeof value === 'string' ? value : '';

    const accept = esVideo
        ? 'video/mp4,video/webm'
        : 'image/png,image/jpeg,image/jpg,image/webp,image/svg+xml,image/gif';

    const specs = esVideo
        ? `Video MP4/WEBM · para que no se vea pixelado. ${TEXTO_REQUISITO}`
        : `Imagen PNG/JPG/WEBP/SVG/GIF · para que no se vea pixelada. ${TEXTO_REQUISITO} · se reescala a ${MAX_LADO_IMAGEN} px si es más grande.`;

    const avisoResolucion = (medido, width, height) => {
        MySwal.fire(
            'Resolución insuficiente',
            `${medido} tiene ${width}×${height} px y se vería pixelado en la pantalla.<br/><br/>${TEXTO_REQUISITO}`,
            'warning'
        );
    };

    const subir = async (archivo) => {
        const data = new FormData();
        data.append('archivo', archivo);
        const resp = await serverApi.post('/videos/upload-media', data, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return resp.data;
    };

    const procesar = async (file) => {
        if (!file || disabled) return;

        setInfo(null);

        try {
            setUploading(true);

            let archivoASubir = file;

            if (esVideo) {
                try {
                    const dims = await leerDimensionesVideo(file);
                    if (!dimensionesValidas(dims.width, dims.height)) {
                        avisoResolucion('El video', dims.width, dims.height);
                        return;
                    }
                    setInfo({ width: dims.width, height: dims.height, optimizada: false });
                } catch (err) {
                    // Si el navegador no puede leer la resolución, se sube igual
                    console.warn('No se pudo verificar la resolución del video:', err);
                    setInfo({ noVerificado: true });
                }
            } else {
                const res = await prepararImagen(file);
                if (!res.valida) {
                    avisoResolucion('La imagen', res.width, res.height);
                    return;
                }
                archivoASubir = res.file;
                setInfo({ width: res.width, height: res.height, optimizada: res.optimizada });
            }

            const data = await subir(archivoASubir);

            if (data.ok) {
                onChange(data.url, data.tipoMedia);
            } else {
                MySwal.fire('Error', data.msg || 'No se pudo subir el archivo', 'error');
            }
        } catch (error) {
            const msg = error?.response?.data?.msg || 'No se pudo subir el archivo';
            MySwal.fire('Error', msg, 'error');
        } finally {
            setUploading(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            procesar(e.dataTransfer.files[0]);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        if (!disabled) setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleFileSelect = (e) => {
        if (e.target.files && e.target.files[0]) {
            procesar(e.target.files[0]);
        }
        e.target.value = '';
    };

    return (
        <div style={{ marginBottom: 4 }}>
            {label && (
                <label style={{ fontSize: 12, fontWeight: 700, color: '#475569', display: 'block', marginBottom: 4 }}>
                    {label}
                </label>
            )}

            {/* Zona Drag and Drop / Preview */}
            <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                style={{
                    border: `2px dashed ${isDragging ? 'var(--color-primario)' : '#cbd5e1'}`,
                    borderRadius: 14,
                    background: isDragging ? '#f0fdf4' : '#f8fafc',
                    padding: 16,
                    textAlign: 'center',
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.6 : 1,
                }}
            >
                {!safeValue && !uploading && !disabled && (
                    <input
                        type="file"
                        accept={accept}
                        onChange={handleFileSelect}
                        style={{
                            position: 'absolute',
                            top: 0, left: 0, width: '100%', height: '100%',
                            opacity: 0, cursor: 'pointer', zIndex: 1
                        }}
                    />
                )}

                {uploading ? (
                    <div style={{ padding: 12, color: 'var(--color-primario-texto)', fontWeight: 700 }}>
                        <span className="spinner-border spinner-border-sm me-2"></span> Procesando y subiendo...
                    </div>
                ) : safeValue ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                        {esVideo ? (
                            <video
                                src={urlMedia(safeValue)}
                                muted
                                style={{ maxHeight: 120, maxWidth: '100%', borderRadius: 8, background: '#000', border: '1px solid #e2e8f0' }}
                            />
                        ) : (
                            <img
                                src={urlMedia(safeValue)}
                                alt="preview"
                                onError={(e) => { e.target.style.visibility = 'hidden'; }}
                                style={{ maxHeight: 120, maxWidth: '100%', objectFit: 'contain', borderRadius: 8, background: '#ffffff', padding: 4, border: '1px solid #e2e8f0' }}
                            />
                        )}

                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
                            <span style={{ fontSize: 12, fontWeight: 700, color: '#16a34a' }}>
                                ✓ Archivo cargado
                            </span>

                            {info && info.width && info.height && (
                                <span style={{ fontSize: 11, color: '#64748b' }}>
                                    <i className="fa-solid fa-ruler-combined me-1"></i>
                                    {info.width}×{info.height} px
                                </span>
                            )}
                            {info && info.noVerificado && (
                                <span style={{ fontSize: 11, color: '#b45309' }}>
                                    No se pudo verificar la resolución
                                </span>
                            )}
                            {info && info.optimizada && (
                                <span style={{ fontSize: 11, color: '#16a34a', fontWeight: 700 }}>
                                    <i className="fa-solid fa-wand-magic-sparkles me-1"></i>Optimizada
                                </span>
                            )}

                            <button
                                type="button"
                                disabled={disabled}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setInfo(null);
                                    onChange('');
                                }}
                                style={{
                                    background: '#fee2e2', border: '1px solid #fca5a5', color: '#b91c1c',
                                    borderRadius: 8, padding: '4px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer'
                                }}
                            >
                                Cambiar / Quitar
                            </button>
                        </div>
                    </div>
                ) : (
                    <div>
                        <div style={{ fontSize: 24, marginBottom: 4, color: 'var(--color-primario-texto)' }}>
                            <i className={esVideo ? 'fa-solid fa-film' : 'fa-solid fa-image'}></i>
                        </div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                            Arrastra y suelta tu archivo aquí o <span style={{ color: 'var(--color-primario-texto)', textDecoration: 'underline' }}>examina</span>
                        </div>
                        <div style={specStyle}>{specs}</div>
                    </div>
                )}
            </div>
        </div>
    );
};
